'use client';

import { useEffect, useRef, useState } from 'react';

type Status = 'idle' | 'playing' | 'paused';

export default function BrowserTextToSpeech() {
  const [supported, setSupported] = useState<boolean | null>(null);
  const [text, setText] = useState('');
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [voiceName, setVoiceName] = useState('');
  const [rate, setRate] = useState(1);
  const [pitch, setPitch] = useState(1);
  const [status, setStatus] = useState<Status>('idle');
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  useEffect(() => {
    const hasSpeech = typeof window !== 'undefined' && 'speechSynthesis' in window;
    const markSupport = () => setSupported(hasSpeech);
    markSupport();
    if (!hasSpeech) return;

    const synth = window.speechSynthesis;
    const loadVoices = () => {
      const next = synth.getVoices();
      if (next.length > 0) {
        setVoices(next);
        setVoiceName((current) => {
          if (current && next.some((v) => v.name === current)) return current;
          const preferred = next.find((v) => v.default) ?? next[0];
          return preferred ? preferred.name : '';
        });
      }
    };

    loadVoices();
    synth.addEventListener('voiceschanged', loadVoices);
    return () => {
      synth.removeEventListener('voiceschanged', loadVoices);
      synth.cancel();
    };
  }, []);

  function speak() {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    const synth = window.speechSynthesis;

    if (status === 'paused') {
      synth.resume();
      setStatus('playing');
      return;
    }

    if (text.trim() === '') return;
    synth.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    const voice = voices.find((v) => v.name === voiceName);
    if (voice) {
      utterance.voice = voice;
      utterance.lang = voice.lang;
    }
    utterance.rate = rate;
    utterance.pitch = pitch;
    utterance.onend = () => setStatus('idle');
    utterance.onerror = () => setStatus('idle');
    utteranceRef.current = utterance;
    synth.speak(utterance);
    setStatus('playing');
  }

  function pause() {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    window.speechSynthesis.pause();
    setStatus('paused');
  }

  function stop() {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    setStatus('idle');
  }

  const hasInput = text.trim().length > 0;

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Text to speech</span>
        <h2 className="tool-live-title">Text to Speech</h2>
        <p className="tool-live-subtitle">
          Turn any text into spoken audio with your browser&apos;s built-in voices. Pick a voice,
          tune the rate and pitch, and press play.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Your text</span>
              <textarea
                onChange={(e) => setText(e.target.value)}
                placeholder="Type or paste the text you want read aloud..."
                rows={12}
                value={text}
              />
            </label>

            {supported ? (
              <>
                <label className="tool-live-field tool-live-field-full">
                  <span>Voice</span>
                  <select onChange={(e) => setVoiceName(e.target.value)} value={voiceName}>
                    {voices.length === 0 ? (
                      <option value="">Loading voices…</option>
                    ) : (
                      voices.map((voice) => (
                        <option key={voice.name} value={voice.name}>
                          {voice.name} ({voice.lang}){voice.default ? ' — default' : ''}
                        </option>
                      ))
                    )}
                  </select>
                </label>
                <label className="tool-live-field">
                  <span>Rate: {rate.toFixed(1)}x</span>
                  <input
                    max={2}
                    min={0.5}
                    onChange={(e) => setRate(Number(e.target.value))}
                    step={0.1}
                    type="range"
                    value={rate}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Pitch: {pitch.toFixed(1)}</span>
                  <input
                    max={2}
                    min={0}
                    onChange={(e) => setPitch(Number(e.target.value))}
                    step={0.1}
                    type="range"
                    value={pitch}
                  />
                </label>
              </>
            ) : null}
          </div>

          {supported ? (
            <div className="tool-live-actions">
              <button
                className="button button-secondary"
                disabled={!hasInput && status !== 'paused'}
                onClick={speak}
                type="button"
              >
                {status === 'paused' ? 'Resume' : 'Play'}
              </button>
              <button
                className="button button-secondary"
                disabled={status !== 'playing'}
                onClick={pause}
                type="button"
              >
                Pause
              </button>
              <button
                className="button button-secondary"
                disabled={status === 'idle'}
                onClick={stop}
                type="button"
              >
                Stop
              </button>
            </div>
          ) : null}
        </section>

        <section className="tool-live-preview">
          {supported === false ? (
            <p className="status-message status-error">
              This browser does not support the Web Speech API, so text-to-speech is unavailable.
              Try the latest Chrome, Edge, or Safari.
            </p>
          ) : (
            <div
              style={{
                border: '1px solid var(--border, rgba(148,163,184,0.25))',
                borderRadius: '0.5rem',
                padding: '0.9rem 1rem',
              }}
            >
              <div style={{ fontSize: '0.72rem', color: 'var(--muted, #94a3b8)' }}>Status</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, textTransform: 'capitalize' }}>
                {status}
              </div>
              <p
                style={{ marginTop: '0.5rem', color: 'var(--muted, #94a3b8)', fontSize: '0.85rem' }}
              >
                {voices.length > 0
                  ? `${voices.length} voices available from your browser and operating system.`
                  : 'Voices load from your browser and operating system.'}
              </p>
            </div>
          )}
          <p className="tool-live-panel-note">
            Spoken locally by your browser; nothing is uploaded.
          </p>
        </section>
      </div>
    </div>
  );
}
